import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
    FiArrowLeft,
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiMapPin,
    FiUser,
    FiXCircle,
    FiDollarSign,
} from "react-icons/fi";

import {
    getTaDaClaim,
} from "services/api/taDaApi";

import {
    showError,
} from "utils/toast";


export default function TaDaDetail() {

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    const { id } = useParams();


    const [claim, setClaim] = useState(null);


    const [
        loading,
        setLoading,
    ] = useState(true);



    /*
    |--------------------------------------------------------------------------
    | LOAD CLAIM
    |--------------------------------------------------------------------------
    */

    const loadClaim =
        async () => {

            try {

                setLoading(true);


                const response =
                    await getTaDaClaim(id);


                setClaim(
                    response?.data?.claim || null
                );

            }
            catch (error) {

                console.error(
                    "TA/DA detail error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to load TA/DA claim."
                );

            }
            finally {

                setLoading(false);

            }

        };


    useEffect(() => {

        loadClaim();

    }, [id]);



    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="mt-5 flex justify-center py-20">

                <div
                    className="
                        h-10
                        w-10
                        animate-spin
                        rounded-full
                        border-4
                        border-gray-200
                        border-t-blue-600
                    "
                />

            </div>

        );

    }



    /*
    |--------------------------------------------------------------------------
    | NOT FOUND
    |--------------------------------------------------------------------------
    */

    if (!claim) {

        return (

            <div className="mt-5">

                <div
                    className="
                        rounded-xl
                        border
                        border-red-200
                        bg-red-50
                        p-6
                        text-red-700
                    "
                >
                    TA/DA claim not found.
                </div>

            </div>

        );

    }



    /*
    |--------------------------------------------------------------------------
    | STATUS
    |--------------------------------------------------------------------------
    */

    const getStatusClass =
        (status) => {

            switch (status) {

                case "draft":
                    return "bg-gray-100 text-gray-700";

                case "submitted":
                    return "bg-blue-100 text-blue-700";

                case "approved":
                    return "bg-green-100 text-green-700";

                case "rejected":
                    return "bg-red-100 text-red-700";

                case "paid":
                    return "bg-emerald-100 text-emerald-700";

                case "cancelled":
                    return "bg-gray-200 text-gray-600";

                default:
                    return "bg-gray-100 text-gray-700";

            }

        };



    /*
    |--------------------------------------------------------------------------
    | HELPERS
    |--------------------------------------------------------------------------
    */

    const formatDate =
        (value) => {

            if (!value) {
                return "-";
            }

            return new Date(
                value
            ).toLocaleDateString();

        };


    const formatDateTime =
        (value) => {

            if (!value) {
                return "-";
            }

            return new Date(
                value
            ).toLocaleString();

        };


    const formatMoney =
        (value) => {

            if (
                value === null ||
                value === undefined
            ) {

                return "-";

            }

            return `₹${value}`;

        };



    return (

        <div className="mt-5 space-y-5">


            {/* =========================================================
                HEADER
            ========================================================== */}

            <div
                className="
                    flex
                    flex-col
                    gap-4
                    rounded-2xl
                    bg-white
                    p-5
                    shadow-sm
                    dark:bg-navy-800
                    md:flex-row
                    md:items-center
                    md:justify-between
                "
            >

                <div>

                    <div
                        className="
                            mb-2
                            flex
                            items-center
                            gap-2
                        "
                    >

                        <Link
                            to="/admin/ta-da"
                            className="
                                inline-flex
                                h-9
                                w-9
                                items-center
                                justify-center
                                rounded-lg
                                bg-gray-100
                                text-gray-700
                                hover:bg-gray-200
                            "
                            title="Back"
                        >
                            <FiArrowLeft />
                        </Link>


                        <span
                            className="
                                text-sm
                                text-gray-500
                            "
                        >
                            TA/DA Claim
                        </span>

                    </div>


                    <h1
                        className="
                            text-2xl
                            font-bold
                            text-navy-700
                            dark:text-white
                        "
                    >
                        {claim.title || "TA/DA Claim"}
                    </h1>


                    <p
                        className="
                            mt-1
                            text-sm
                            text-gray-500
                        "
                    >
                        Claim #{claim.id}
                    </p>

                </div>


                <span
                    className={`
                        inline-flex
                        w-fit
                        items-center
                        rounded-full
                        px-4
                        py-2
                        text-sm
                        font-semibold
                        capitalize
                        ${getStatusClass(
                            claim.status
                        )}
                    `}
                >

                    {claim.status?.replace(
                        /_/g,
                        " "
                    )}

                </span>

            </div>



            {/* =========================================================
                SUMMARY CARDS
            ========================================================== */}

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-2
                    xl:grid-cols-4
                "
            >

                <SummaryCard
                    icon={<FiDollarSign />}
                    title="Claimed Amount"
                    value={formatMoney(
                        claim.claimedAmount
                    )}
                />


                <SummaryCard
                    icon={<FiCheckCircle />}
                    title="Approved Amount"
                    value={formatMoney(
                        claim.approvedAmount
                    )}
                />


                <SummaryCard
                    icon={<FiClock />}
                    title="Claim Date"
                    value={formatDate(
                        claim.claimDate
                    )}
                />


                <SummaryCard
                    icon={<FiUser />}
                    title="Employee"
                    value={
                        claim.employee?.name ||
                        "-"
                    }
                />

            </div>



            {/* =========================================================
                BASIC INFORMATION
            ========================================================== */}

            <div
                className="
                    rounded-2xl
                    bg-white
                    p-5
                    shadow-sm
                    dark:bg-navy-800
                "
            >

                <SectionTitle>
                    Claim Information
                </SectionTitle>


                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                        lg:grid-cols-3
                    "
                >

                    <InfoField
                        label="Employee"
                        value={
                            claim.employee?.name
                        }
                    />


                    <InfoField
                        label="Employee Code"
                        value={
                            claim.employee?.employeeCode
                        }
                    />


                    <InfoField
                        label="Phone"
                        value={
                            claim.employee?.phone
                        }
                    />


                    <InfoField
                        label="Claim Date"
                        value={
                            formatDate(
                                claim.claimDate
                            )
                        }
                    />


                    <InfoField
                        label="From Date"
                        value={
                            formatDate(
                                claim.fromDate
                            )
                        }
                    />


                    <InfoField
                        label="To Date"
                        value={
                            formatDate(
                                claim.toDate
                            )
                        }
                    />


                    <InfoField
                        label="Claimed Amount"
                        value={
                            formatMoney(
                                claim.claimedAmount
                            )
                        }
                    />


                    <InfoField
                        label="Approved Amount"
                        value={
                            formatMoney(
                                claim.approvedAmount
                            )
                        }
                    />


                    <InfoField
                        label="Submitted At"
                        value={
                            formatDateTime(
                                claim.submittedAt
                            )
                        }
                    />


                    <InfoField
                        label="Approved At"
                        value={
                            formatDateTime(
                                claim.approvedAt
                            )
                        }
                    />


                    <InfoField
                        label="Paid At"
                        value={
                            formatDateTime(
                                claim.paidAt
                            )
                        }
                    />


                    <InfoField
                        label="Payment Reference"
                        value={
                            claim.paymentRefNo
                        }
                    />

                </div>


                {claim.description && (

                    <div className="mt-5">

                        <InfoField
                            label="Description"
                            value={
                                claim.description
                            }
                            full
                        />

                    </div>

                )}


                {claim.remarks && (

                    <div className="mt-5">

                        <InfoField
                            label="Remarks"
                            value={
                                claim.remarks
                            }
                            full
                        />

                    </div>

                )}


                {claim.rejectionReason && (

                    <div
                        className="
                            mt-5
                            rounded-xl
                            border
                            border-red-200
                            bg-red-50
                            p-4
                        "
                    >

                        <div
                            className="
                                flex
                                items-center
                                gap-2
                                font-semibold
                                text-red-700
                            "
                        >

                            <FiXCircle />

                            Rejection / Correction Reason

                        </div>


                        <p
                            className="
                                mt-2
                                text-sm
                                text-red-700
                            "
                        >
                            {
                                claim.rejectionReason
                            }
                        </p>

                    </div>

                )}

            </div>



            {/* =========================================================
                CLAIM ITEMS
            ========================================================== */}

            <div
                className="
                    rounded-2xl
                    bg-white
                    p-5
                    shadow-sm
                    dark:bg-navy-800
                "
            >

                <div
                    className="
                        mb-4
                        flex
                        items-center
                        justify-between
                    "
                >

                    <SectionTitle>
                        Claim Items
                    </SectionTitle>


                    <span
                        className="
                            rounded-full
                            bg-blue-50
                            px-3
                            py-1
                            text-xs
                            font-semibold
                            text-blue-700
                        "
                    >
                        {claim.items?.length || 0} Items
                    </span>

                </div>


                {claim.items?.length ? (

                    <div className="space-y-4">

                        {claim.items.map(
                            (
                                claimItem,
                                index
                            ) => (

                                <div
                                    key={
                                        claimItem.id ||
                                        index
                                    }
                                    className="
                                        rounded-xl
                                        border
                                        border-gray-200
                                        p-4
                                    "
                                >

                                    {/* ITEM HEADER */}

                                    <div
                                        className="
                                            flex
                                            flex-col
                                            gap-3
                                            md:flex-row
                                            md:items-center
                                            md:justify-between
                                        "
                                    >

                                        <div>

                                            <div
                                                className="
                                                    text-base
                                                    font-semibold
                                                    text-navy-700
                                                "
                                            >
                                                {claimItem.title}
                                            </div>


                                            <div
                                                className="
                                                    mt-1
                                                    text-xs
                                                    text-gray-500
                                                "
                                            >
                                                {formatDate(
                                                    claimItem.itemDate
                                                )}
                                                {" • "}
                                                {
                                                    claimItem.ruleType
                                                        ?.replace(
                                                            /_/g,
                                                            " "
                                                        )
                                                }
                                            </div>

                                        </div>


                                        <div
                                            className="
                                                flex
                                                gap-2
                                            "
                                        >

                                            <AmountBadge
                                                label="Claimed"
                                                value={
                                                    formatMoney(
                                                        claimItem.claimedAmount
                                                    )
                                                }
                                            />


                                            <AmountBadge
                                                label="Approved"
                                                value={
                                                    formatMoney(
                                                        claimItem.approvedAmount
                                                    )
                                                }
                                            />

                                        </div>

                                    </div>



                                    {/* ITEM DETAILS */}

                                    <div
                                        className="
                                            mt-4
                                            grid
                                            grid-cols-1
                                            gap-4
                                            md:grid-cols-2
                                            lg:grid-cols-3
                                        "
                                    >

                                        <InfoField
                                            label="Rule"
                                            value={
                                                claimItem.rule?.name
                                            }
                                        />


                                        <InfoField
                                            label="Rule Amount"
                                            value={
                                                formatMoney(
                                                    claimItem.rule?.amount
                                                )
                                            }
                                        />


                                        <InfoField
                                            label="Distance"
                                            value={
                                                claimItem.distanceKm
                                                    ? `${claimItem.distanceKm} KM`
                                                    : "-"
                                            }
                                        />


                                        <InfoField
                                            label="From"
                                            value={
                                                claimItem.fromLocation
                                            }
                                        />


                                        <InfoField
                                            label="To"
                                            value={
                                                claimItem.toLocation
                                            }
                                        />

                                    </div>



                                    {/* ITEM DESCRIPTION */}

                                    {claimItem.description && (

                                        <div
                                            className="
                                                mt-4
                                                rounded-lg
                                                bg-gray-50
                                                p-3
                                            "
                                        >

                                            <div
                                                className="
                                                    mb-1
                                                    text-xs
                                                    font-semibold
                                                    uppercase
                                                    text-gray-500
                                                "
                                            >
                                                Description
                                            </div>

                                            <div
                                                className="
                                                    text-sm
                                                    text-gray-700
                                                "
                                            >
                                                {
                                                    claimItem.description
                                                }
                                            </div>

                                        </div>

                                    )}



                                    {/* ITEM REMARKS */}

                                    {claimItem.remarks && (

                                        <div
                                            className="
                                                mt-3
                                                text-sm
                                                text-gray-600
                                            "
                                        >
                                            <strong>
                                                Remarks:
                                            </strong>
                                            {" "}
                                            {
                                                claimItem.remarks
                                            }
                                        </div>

                                    )}



                                    {/* PROOF */}

                                    {claimItem.proofFile && (

                                        <div
                                            className="
                                                mt-4
                                                flex
                                                flex-wrap
                                                items-center
                                                gap-3
                                                rounded-lg
                                                border
                                                border-blue-100
                                                bg-blue-50
                                                p-3
                                            "
                                        >

                                            <FiFileText
                                                className="
                                                    text-blue-600
                                                "
                                            />


                                            <div
                                                className="
                                                    min-w-0
                                                    flex-1
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-xs
                                                        font-semibold
                                                        text-blue-700
                                                    "
                                                >
                                                    Proof File
                                                </div>


                                                <div
                                                    className="
                                                        break-all
                                                        text-xs
                                                        text-gray-600
                                                    "
                                                >
                                                    {
                                                        claimItem.proofFile
                                                    }
                                                </div>

                                            </div>


                                           <a
                                                href={`${API_URL}/uploads/${claimItem.proofFile}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="
                                                    rounded-lg
                                                    bg-blue-600
                                                    px-3
                                                    py-2
                                                    text-xs
                                                    font-semibold
                                                    text-white
                                                    hover:bg-blue-700
                                                "
                                            >
                                                View Proof
                                            </a>

                                        </div>

                                    )}

                                </div>

                            )
                        )}

                    </div>

                ) : (

                    <div
                        className="
                            rounded-xl
                            bg-gray-50
                            p-8
                            text-center
                            text-sm
                            text-gray-500
                        "
                    >
                        No claim items found.
                    </div>

                )}

            </div>



            {/* =========================================================
                APPROVAL HISTORY
            ========================================================== */}

            <div
                className="
                    rounded-2xl
                    bg-white
                    p-5
                    shadow-sm
                    dark:bg-navy-800
                "
            >

                <SectionTitle>
                    Approval History
                </SectionTitle>


                {claim.approvals?.length ? (

                    <div className="space-y-3">

                        {claim.approvals.map(
                            (
                                approval,
                                index
                            ) => (

                                <div
                                    key={
                                        approval.id ||
                                        index
                                    }
                                    className="
                                        flex
                                        flex-col
                                        gap-2
                                        rounded-xl
                                        border
                                        border-gray-200
                                        p-4
                                        md:flex-row
                                        md:items-center
                                        md:justify-between
                                    "
                                >

                                    <div>

                                        <div
                                            className="
                                                flex
                                                items-center
                                                gap-2
                                                text-sm
                                                font-semibold
                                            "
                                        >

                                            {approval.newStatus ===
                                                "approved" ? (

                                                <FiCheckCircle
                                                    className="
                                                        text-green-600
                                                    "
                                                />

                                            ) : (

                                                <FiXCircle
                                                    className="
                                                        text-red-600
                                                    "
                                                />

                                            )}

                                            <span
                                                className="
                                                    capitalize
                                                "
                                            >
                                                {
                                                    approval.oldStatus
                                                }
                                                {" → "}
                                                {
                                                    approval.newStatus
                                                }
                                            </span>

                                        </div>


                                        {approval.remarks && (

                                            <p
                                                className="
                                                    mt-1
                                                    text-sm
                                                    text-gray-600
                                                "
                                            >
                                                {
                                                    approval.remarks
                                                }
                                            </p>

                                        )}

                                    </div>


                                    <div
                                        className="
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        {
                                            formatDateTime(
                                                approval.createdAt
                                            )
                                        }
                                    </div>

                                </div>

                            )
                        )}

                    </div>

                ) : (

                    <div
                        className="
                            rounded-xl
                            bg-gray-50
                            p-8
                            text-center
                            text-sm
                            text-gray-500
                        "
                    >
                        No approval history yet.
                    </div>

                )}

            </div>

        </div>

    );

}



/*
|--------------------------------------------------------------------------
| SUMMARY CARD
|--------------------------------------------------------------------------
*/

function SummaryCard({
    icon,
    title,
    value,
}) {

    return (

        <div
            className="
                rounded-2xl
                bg-white
                p-5
                shadow-sm
                dark:bg-navy-800
            "
        >

            <div
                className="
                    mb-3
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center
                    rounded-xl
                    bg-blue-50
                    text-blue-600
                "
            >
                {icon}
            </div>


            <div
                className="
                    text-sm
                    text-gray-500
                "
            >
                {title}
            </div>


            <div
                className="
                    mt-1
                    text-xl
                    font-bold
                    text-navy-700
                    dark:text-white
                "
            >
                {value || "-"}
            </div>

        </div>

    );

}



/*
|--------------------------------------------------------------------------
| SECTION TITLE
|--------------------------------------------------------------------------
*/

function SectionTitle({
    children,
}) {

    return (

        <h2
            className="
                text-lg
                font-bold
                text-navy-700
                dark:text-white
            "
        >
            {children}
        </h2>

    );

}



/*
|--------------------------------------------------------------------------
| INFO FIELD
|--------------------------------------------------------------------------
*/

function InfoField({
    label,
    value,
    full = false,
}) {

    return (

        <div
            className={
                full
                    ? "w-full"
                    : ""
            }
        >

            <div
                className="
                    text-xs
                    font-semibold
                    uppercase
                    tracking-wide
                    text-gray-500
                "
            >
                {label}
            </div>


            <div
                className="
                    mt-1
                    break-words
                    text-sm
                    font-medium
                    text-gray-800
                    dark:text-gray-200
                "
            >
                {value || "-"}
            </div>

        </div>

    );

}



/*
|--------------------------------------------------------------------------
| AMOUNT BADGE
|--------------------------------------------------------------------------
*/

function AmountBadge({
    label,
    value,
}) {

    return (

        <div
            className="
                rounded-lg
                bg-gray-50
                px-3
                py-2
                text-right
            "
        >

            <div
                className="
                    text-[10px]
                    font-semibold
                    uppercase
                    text-gray-500
                "
            >
                {label}
            </div>


            <div
                className="
                    text-sm
                    font-bold
                    text-navy-700
                "
            >
                {value}
            </div>

        </div>

    );

}